import React, { useState, useEffect } from 'react';
import { 
  Users, Crown, TrendingUp, UserPlus, RefreshCw, 
  MessageSquare, DollarSign, MousePointer, BarChart3 
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { supabase } from '../../lib/supabase';

const AdminDashboard = () => {
  const { apiRequest } = useAdminAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    premiumUsers: 0,
    freeUsers: 0,
    deletedUsers: 0,
    onboardingRate: 0,
    onboardedCount: 0,
    newThisWeek: 0,
    newThisMonth: 0,
    // New metrics
    totalQueries: 0,
    queriesThisWeek: 0,
    queriesThisMonth: 0,
    avgQueriesPerUser: 0,
    // Revenue (placeholder for future)
    revenueThisMonth: 0,
    affiliateClicks: 0,
  });
  const [recentSignups, setRecentSignups] = useState([]);
  const [queryBreakdown, setQueryBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadDashboardData = async () => {
    try {
      // Use admin API to bypass RLS
      const response = await apiRequest('/api/admin/users?status=all&limit=500');
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      const users = data.users || [];

      const activeUsers = users.filter(u => !u.deleted_at);
      const deletedUsers = users.filter(u => u.deleted_at);
      const premiumUsers = activeUsers.filter(u => u.plan_type === 'premium');
      const freeUsers = activeUsers.filter(u => u.plan_type !== 'premium');
      const onboardedUsers = activeUsers.filter(u => u.onboarding_completed);

      // Calculate date ranges
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const newThisWeek = activeUsers.filter(u => new Date(u.created_at) >= oneWeekAgo);
      const newThisMonth = activeUsers.filter(u => new Date(u.created_at) >= oneMonthAgo);

      // Calculate query stats from user data
      const totalQueries = activeUsers.reduce((sum, u) => sum + (u.queries_total || 0), 0);
      const queriesThisMonth = activeUsers.reduce((sum, u) => sum + (u.queries_used_month || 0), 0);
      const avgQueriesPerUser = activeUsers.length > 0 ? totalQueries / activeUsers.length : 0;

      // Get query stats from chat_logs (RLS disabled on this table)
      let queriesThisWeek = 0;
      let breakdown = [];
      let affiliateClicks = 0;

      try {
        // Get queries this week
        const { count: weekCount } = await supabase
          .from('chat_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', oneWeekAgo.toISOString());
        queriesThisWeek = weekCount || 0;

        // Get query category breakdown
        const { data: categoryData } = await supabase
          .from('chat_logs')
          .select('query_category')
          .gte('created_at', oneMonthAgo.toISOString());
        
        if (categoryData) {
          const counts = {};
          categoryData.forEach(log => {
            const cat = log.query_category || 'general';
            counts[cat] = (counts[cat] || 0) + 1;
          });
          breakdown = Object.entries(counts)
            .map(([category, count]) => ({ category, count }))
            .sort((a, b) => b.count - a.count);
        }

        // Get affiliate clicks
        const { count: clickCount } = await supabase
          .from('affiliate_clicks')
          .select('*', { count: 'exact', head: true })
          .gte('clicked_at', oneMonthAgo.toISOString());
        affiliateClicks = clickCount || 0;

      } catch (e) {
        console.log('Could not load chat_logs stats:', e);
        // Fallback: estimate queries this week
        queriesThisWeek = Math.round(queriesThisMonth / 4);
      }

      setStats({
        totalUsers: activeUsers.length,
        premiumUsers: premiumUsers.length,
        freeUsers: freeUsers.length,
        deletedUsers: deletedUsers.length,
        onboardingRate: activeUsers.length > 0 
          ? Math.round((onboardedUsers.length / activeUsers.length) * 100) 
          : 0,
        onboardedCount: onboardedUsers.length,
        newThisWeek: newThisWeek.length,
        newThisMonth: newThisMonth.length,
        totalQueries,
        queriesThisWeek,
        queriesThisMonth,
        avgQueriesPerUser: Math.round(avgQueriesPerUser * 10) / 10,
        revenueThisMonth: 0,
        affiliateClicks,
      });

      setQueryBreakdown(breakdown);

      // Get recent signups (last 5)
      const recent = activeUsers
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
        .map(u => ({
          id: u.id,
          email: u.email,
          name: u.first_name || u.full_name?.split(' ')[0] || '',
          plan: u.plan_type || 'free',
          date: new Date(u.created_at).toLocaleDateString(),
          onboarded: u.onboarding_completed
        }));

      setRecentSignups(recent);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'purple' }) => {
    const colorClasses = {
      purple: 'border-purple-500/30 bg-purple-500/10',
      green: 'border-green-500/30 bg-green-500/10',
      blue: 'border-blue-500/30 bg-blue-500/10',
      yellow: 'border-yellow-500/30 bg-yellow-500/10',
      red: 'border-red-500/30 bg-red-500/10',
    };
    const iconColors = {
      purple: 'text-purple-400',
      green: 'text-green-400',
      blue: 'text-blue-400',
      yellow: 'text-yellow-400',
      red: 'text-red-400',
    };

    return (
      <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className={`w-5 h-5 ${iconColors[color]}`} />
          </div>
          <div>
            <p className="text-sm text-gray-400">{label}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subValue && (
              <p className="text-xs text-gray-500">{subValue}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const formatCategory = (cat) => {
    const labels = {
      where_to_watch: 'Where to Watch',
      optimization: 'Optimization',
      recommendation: 'Recommendations',
      live_sports: 'Live Sports',
      general: 'General',
    };
    return labels[cat] || cat;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400">Overview of your Stomp users and activity</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Main Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats.totalUsers}
          subValue={`${stats.deletedUsers} deleted`}
          color="purple"
        />
        <StatCard
          icon={Crown}
          label="Premium Users"
          value={stats.premiumUsers}
          subValue={`${stats.freeUsers} free`}
          color="green"
        />
        <StatCard
          icon={TrendingUp}
          label="Onboarding Rate"
          value={`${stats.onboardingRate}%`}
          subValue={`${stats.onboardedCount} completed`}
          color="blue"
        />
        <StatCard
          icon={UserPlus}
          label="New This Week"
          value={stats.newThisWeek}
          subValue={`${stats.newThisMonth} this month`}
          color="yellow"
        />
      </div>

      {/* Query Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={MessageSquare}
          label="Total Queries"
          value={stats.totalQueries.toLocaleString()}
          subValue="all time"
          color="purple"
        />
        <StatCard
          icon={MessageSquare}
          label="Queries This Week"
          value={stats.queriesThisWeek.toLocaleString()}
          subValue={`${stats.queriesThisMonth.toLocaleString()} this month`}
          color="blue"
        />
        <StatCard
          icon={BarChart3}
          label="Avg Queries/User"
          value={stats.avgQueriesPerUser}
          subValue="per user"
          color="green"
        />
        <StatCard
          icon={MousePointer}
          label="Affiliate Clicks"
          value={stats.affiliateClicks}
          subValue="this month"
          color="yellow"
        />
      </div>

      {/* Revenue Row (for future) */}
      {stats.revenueThisMonth > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={DollarSign}
            label="Revenue MTD"
            value={`$${stats.revenueThisMonth.toFixed(2)}`}
            subValue="this month"
            color="green"
          />
        </div>
      )}

      {/* Bottom Row: Recent Signups + Query Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Signups */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Signups</h2>
          <div className="space-y-3">
            {recentSignups.map(user => (
              <div key={user.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
                    {user.email[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-medium">{user.email}</p>
                    <p className="text-sm text-gray-400">
                      <span className="mr-2">📅 {user.date}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded ${
                    user.plan === 'premium' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {user.plan}
                  </span>
                  {user.onboarded && (
                    <span className="px-2 py-1 text-xs rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                      Onboarded
                    </span>
                  )}
                </div>
              </div>
            ))}
            {recentSignups.length === 0 && (
              <p className="text-gray-500 text-center py-4">No signups yet</p>
            )}
          </div>
        </div>

        {/* Query Breakdown */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-white mb-4">Query Categories (This Month)</h2>
          <div className="space-y-3">
            {queryBreakdown.length > 0 ? (
              queryBreakdown.map(({ category, count }) => {
                const total = queryBreakdown.reduce((sum, q) => sum + q.count, 0);
                const percentage = total > 0 ? (count / total) * 100 : 0;
                
                return (
                  <div key={category} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{formatCategory(category)}</span>
                      <span className="text-gray-400">{count} ({Math.round(percentage)}%)</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">No query data yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;